import { useState } from "react";

const FORM_SUBMIT_URL = "https://readdy.ai/api/form/db6ed1q2sgkaik1une60";

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      params.append(key, String(value));
    });

    try {
      const response = await fetch(FORM_SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam = /spam/i.test(serverMsg);
      const isOk = response.ok && parsed?.code === "OK";

      if (isOk && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch (error) {
      setStatus("error");
      setFormError(error instanceof Error ? error.message : "网络异常，请稍后再试。");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-lg border border-primary-200 bg-primary-100/60 p-6 flex items-start gap-3">
        <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 shrink-0">
          <i className="ri-check-line text-xl text-background-50"></i>
        </span>
        <div>
          <h3 className="text-base font-semibold text-foreground-950 mb-1">留言已提交</h3>
          <p className="text-sm text-foreground-700">
            感谢你的反馈，我们会尽快通过邮箱与你联系。
          </p>
          <button
            type="button"
            onClick={() => setStatus("idle")}
            className="mt-3 text-sm text-primary-700 hover:text-primary-500 transition-colors cursor-pointer"
          >
            再写一条
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      id="about-contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-300 bg-background-50 p-5 md:p-6 space-y-4"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
            称呼
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            maxLength={50}
            placeholder="请输入你的称呼"
            className="w-full bg-background-100 border border-background-300 rounded-md px-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
            邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            maxLength={80}
            placeholder="you@example.com"
            className="w-full bg-background-100 border border-background-300 rounded-md px-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors"
          />
        </div>
      </div>

      <div>
        <label htmlFor="contact-subject" className="block text-sm text-foreground-700 mb-1.5">
          主题
        </label>
        <select
          id="contact-subject"
          name="subject"
          defaultValue="资源反馈"
          className="w-full bg-background-100 border border-background-300 rounded-md px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400 transition-colors cursor-pointer"
        >
          <option value="资源反馈">资源反馈</option>
          <option value="内容建议">内容建议</option>
          <option value="合作洽谈">合作洽谈</option>
          <option value="问题反馈">问题反馈</option>
        </select>
      </div>

      <div>
        <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">
          留言内容
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          placeholder="请输入你的留言（最多 500 字）"
          className="w-full bg-background-100 border border-background-300 rounded-md px-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors resize-none"
        ></textarea>
      </div>

      <input
        className="field-alt-verify"
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status === "error" && formError && (
        <p className="text-sm text-accent-400 flex items-center gap-1.5">
          <i className="ri-error-warning-line"></i>
          {formError}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-400 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {status === "submitting" ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i>
            提交中...
          </>
        ) : (
          <>
            <i className="ri-send-plane-line"></i>
            提交留言
          </>
        )}
      </button>
    </form>
  );
}